import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import { ChevronLeftIcon, ChevronRightIcon } from '@inno-ui/icons'
import cn from 'classnames'
import {
  SCREENSHOT_FALLBACK_SRC,
  handleImageFallback
} from 'modules/download-center/utils/imageFallback'
import { pickLocalizedText } from 'modules/download-center/utils/pickLocalizedText'
import { useTranslation } from 'react-i18next'
import { AppScreenshot, AppVideo } from 'shared/api/containerApi'

import styles from './MediaCarousel.module.scss'

interface Props {
  screenshots?: AppScreenshot[]
  videos?: AppVideo[]
}

interface MediaItem {
  type: 'image' | 'video'
  url: string
  title?: string
  description?: string
}

const buildItems = (
  screenshots: AppScreenshot[] | undefined,
  videos: AppVideo[] | undefined,
  language: string
): MediaItem[] => [
  ...(videos ?? []).map(
    (video): MediaItem => ({
      type: 'video',
      url: video.url,
      title: pickLocalizedText(video.title, language),
      description: pickLocalizedText(video.description, language)
    })
  ),
  ...(screenshots ?? []).map(
    (shot): MediaItem => ({
      type: 'image',
      url: shot.url,
      title: pickLocalizedText(shot.title, language),
      description: pickLocalizedText(shot.description, language)
    })
  )
]

const ITEMS_PER_VIEW = 2
const GAP_PX = 16

export const MediaCarousel: FC<Props> = ({ screenshots, videos }) => {
  const { i18n } = useTranslation()
  const items = buildItems(screenshots, videos, i18n.language)
  const isCircular = items.length > 2
  // With more than two items, a clone of the last item is prepended and a
  // clone of the first is appended. Sliding onto a clone always moves in the
  // direction the user clicked; once the CSS transition finishes landing on
  // a clone, the index is swapped (with the transition disabled for that one
  // frame) to the matching real item, which looks identical — faking an
  // infinite loop.
  const loopItems = isCircular
    ? [items[items.length - 1], ...items, items[0]]
    : items
  const realStartIndex = isCircular ? 1 : 0
  const realEndIndex = isCircular ? items.length : items.length - 1
  const itemsPerView = isCircular
    ? ITEMS_PER_VIEW
    : Math.min(ITEMS_PER_VIEW, items.length) || 1

  const viewportRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  // Pixel math driven by the viewport's real measured width, using native
  // flexbox `gap` for spacing — gap only ever sits *between* items, never
  // trailing after whichever one happens to currently be rightmost, so
  // there's no per-item padding/margin hack that can look asymmetric.
  const itemWidth = Math.max(
    0,
    (viewportWidth - GAP_PX * (itemsPerView - 1)) / itemsPerView
  )
  const pitch = itemWidth + GAP_PX

  const [index, setIndex] = useState(realStartIndex)
  // Starts disabled so the very first paint — before the viewport has been
  // measured and the real pitch is known — never animates a correction;
  // the effect below re-enables it one frame later for real interactions.
  const [transitionEnabled, setTransitionEnabled] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [playingUrl, setPlayingUrl] = useState<string | null>(null)

  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return

    setViewportWidth(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(([entry]) => {
      setViewportWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setIndex(realStartIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  useEffect(() => {
    if (transitionEnabled) return
    const raf = requestAnimationFrame(() => setTransitionEnabled(true))
    return () => cancelAnimationFrame(raf)
  }, [transitionEnabled])

  if (!items.length) {
    return (
      <div className={styles.placeholderRow}>
        {[0].map(i => (
          <div key={i} className={styles.placeholder}>
            <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
              <rect
                x='2'
                y='4'
                width='20'
                height='16'
                rx='2'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <circle cx='8' cy='10' r='1.5' fill='currentColor' />
              <path
                d='M4 17l5-5 3 3 4-4 4 4'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        ))}
      </div>
    )
  }

  // Controls only make sense once there's more content than fits in one
  // view — with <= itemsPerView items everything is already visible.
  const showControls = items.length > itemsPerView
  const prevDisabled = !isCircular && index <= 0
  const nextDisabled = !isCircular && index >= loopItems.length - itemsPerView

  // Ignore extra clicks while a slide is mid-transition, so the index can
  // never be pushed past the leading/trailing clone before it settles.
  const goTo = (nextIndex: number) => {
    if (isAnimating) return
    setIsAnimating(true)
    setTransitionEnabled(true)
    setIndex(nextIndex)
  }

  const handlePrev = () => goTo(index - 1)
  const handleNext = () => goTo(index + 1)

  const handleTransitionEnd = () => {
    setIsAnimating(false)
    if (!isCircular) return
    if (index === 0) {
      setTransitionEnabled(false)
      setIndex(realEndIndex)
    } else if (index === loopItems.length - 1) {
      setTransitionEnabled(false)
      setIndex(realStartIndex)
    }
  }

  return (
    <div className={styles.carousel}>
      {showControls && (
        <Button
          label='Previous'
          isHiddenLabel
          variant={ButtonVariants.Text}
          icon={ChevronLeftIcon}
          disabled={prevDisabled}
          onClick={handlePrev}
        />
      )}

      <div
        className={styles.viewport}
        ref={viewportRef}
        data-testid='media-carousel-viewport'
      >
        <div
          className={styles.track}
          data-testid='media-carousel-track'
          onTransitionEnd={handleTransitionEnd}
          style={{
            transform: `translateX(-${index * pitch}px)`,
            transition: transitionEnabled ? undefined : 'none'
          }}
        >
          {loopItems.map((item, itemIndex) => (
            <div
              key={`${item.type}-${item.url}-${itemIndex}`}
              className={styles.item}
              style={{ width: `${itemWidth}px` }}
            >
              <div className={styles.itemFrame}>
                {item.type === 'image' ? (
                  <>
                    <img
                      src={item.url}
                      alt=''
                      className={styles.media}
                      onError={e =>
                        handleImageFallback(e, SCREENSHOT_FALLBACK_SRC)
                      }
                    />
                    {(item.title || item.description) && (
                      <div className={styles.overlay}>
                        {item.title && (
                          <div className={styles.overlayTitle}>
                            {item.title}
                          </div>
                        )}
                        {item.description && (
                          <div className={styles.overlayDescription}>
                            {item.description}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <video
                      src={item.url}
                      preload='metadata'
                      className={cn(styles.media, styles.video)}
                      onClick={e => {
                        const video = e.currentTarget
                        if (video.paused) {
                          video.controls = true
                          video.play()
                        } else {
                          video.pause()
                        }
                      }}
                      onPlay={() => setPlayingUrl(item.url)}
                      onPause={e => {
                        e.currentTarget.controls = false
                        setPlayingUrl(current =>
                          current === item.url ? null : current
                        )
                      }}
                    />
                    {playingUrl !== item.url && (
                      <>
                        <div className={styles.playIcon} aria-hidden='true'>
                          <svg viewBox='0 0 24 24' fill='currentColor'>
                            <path d='M8 5v14l11-7z' />
                          </svg>
                        </div>
                        {(item.title || item.description) && (
                          <div className={styles.overlay}>
                            {item.title && (
                              <div className={styles.overlayTitle}>
                                {item.title}
                              </div>
                            )}
                            {item.description && (
                              <div className={styles.overlayDescription}>
                                {item.description}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showControls && (
        <Button
          label='Next'
          isHiddenLabel
          variant={ButtonVariants.Text}
          icon={ChevronRightIcon}
          disabled={nextDisabled}
          onClick={handleNext}
        />
      )}
    </div>
  )
}
